import { Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { UUIDService } from '@app/core/services/uuid/uuid.service';
import {
  INotification,
  TNotificationActionType,
} from '@app/core/services/notification/notification.service';

@Component({
  selector: 'ag-notifications-and-alerts',
  templateUrl: './notifications-and-alerts.component.html',
  standalone: false,
})
export class NotificationsAndAlertsComponent {
  private _notificationService = inject(NotificationService);
  private _messageService = inject(MessageService);
  private _uuidService = inject(UUIDService);
  private _notificationQueue: INotification[] = [];
  private _isShowingNotification: boolean = false;

  readonly NOTIFICATION_TOAST_KEY: string = 'notification-toast';
  readonly NEXT_NOTIFICATION_DISPLAY_DELAY: number = 300;

  notifications = toSignal(this._notificationService.notifications$);

  TNotificationActionType = TNotificationActionType;

  constructor() {
    effect(() => {
      this.queueNotifications(this.notifications());
    });
  }

  onClose(): void {
    this._isShowingNotification = false;
    this.showNextNotification();
  }

  queueNotifications(notifications: INotification[]): void {
    if (!notifications?.length) {
      return;
    }

    this._notificationQueue.push(...notifications);

    // If no notification is currently showing, show the next one immediately
    if (!this._isShowingNotification) {
      this.showNextNotification();
    }
  }

  showNotification(notification: INotification): void {
    // Clear any existing notifications with the same key to prevent stacking
    this._messageService.clear(this.NOTIFICATION_TOAST_KEY);

    this._messageService.add({
      key: this.NOTIFICATION_TOAST_KEY,
      id: this._uuidService.generate(),
      summary: notification.title,
      detail: notification.message,
      life: notification.life,
      severity: 'custom',
      styleClass: 'backdrop-blur-lg rounded-xl',
      data: {
        variant: notification.variant,
        action: notification?.action,
      },
    });
  }

  showNextNotification(): void {
    if (this._notificationQueue.length > 0) {
      // Mark as showing immediately to prevent race conditions
      this._isShowingNotification = true;

      const nextNotification: INotification = this._notificationQueue.shift()!;

      // Small delay for better UX when transitioning between notifications
      setTimeout(() => {
        this.showNotification(nextNotification);
      }, this.NEXT_NOTIFICATION_DISPLAY_DELAY);
    }
  }
}
